import { optionsTradesDataType } from '../pages/typings/options.typings'
import { Order, OrderStatus, TransactionType } from '../proto/generated/order'
import { Position } from '../proto/generated/position'
import { OrderDataPrices, OrdersDataType } from '../reducer/orders.reducer'
import { v4 as uuid } from 'uuid'
import store from '../store'
import { Snapshot } from '../reducer/security.reducer'
import { OptionType, Security, SecurityCategory_CategoryCode } from '../proto/generated/security'
import { StoreFutureExpiries } from '../reducer/futures.reducer'
import { getSecurityAsync, getSecurityKey } from './security'
import { fetchOptionChainV2Initial } from './options'
import { exchangeFromJSON } from '../proto/generated/common'
import { calculateGcd, ORDER_TYPES_STRATEGY_AUXILIARY } from './orders'
import { TradeOrder } from '../reducer/strategyBuilder.reducer'
import { AnalyzeGroupedPosition, GroupedPosition, PositionsStateType } from '../reducer/positions.reducer'

/** Multi-leg strategy baskets use ids other than standalone `default` / `default_*` buckets. */
export const isBasketStrategyBasketId = (basketId: string | undefined): boolean =>
    !!basketId && basketId !== 'default' && !basketId.startsWith('default_')

export const getFreshOrdersFromBasketPositions = async (basketPositions: Position[], optionExpiries: optionsTradesDataType[], futuresExpiries:  StoreFutureExpiries, underlyingToken: string, underlyingSnapshot: Snapshot, squarringOff: boolean, openPositionsOnly: boolean = true) => {
    const newOrders: OrdersDataType[] = []
    const newParentSqOffOrder: OrdersDataType[] = []
    const basketOrderPrices: OrderDataPrices[] = []
    for (let i = 0; i < basketPositions.length; i++) {
        const position = basketPositions[i]
        const security = await getSecurityAsync(getSecurityKey(position.security?.exchange, position.security?.code))
        if (!security) continue
        const open = position.closePosition !== 0
        if(openPositionsOnly && !open) continue
        const expiryIndex = optionExpiries.findIndex((e) => Number(e.expirySeconds) === security?.expiry?.expiry)
        const isFuture = security?.categoryCode === SecurityCategory_CategoryCode.FUTURES
        const securityKey = getSecurityKey(position.security?.exchange ?? 0, position.security?.code ?? '')
        const isBuy = position.closePosition > 0;
        const type = squarringOff ? (isBuy ? 'sell' : 'buy') : (isBuy ? 'buy' : 'sell')
        const quantity = position.closePosition;
        let newOrder : OrdersDataType | undefined = undefined

        if(isFuture && futuresExpiries){
            const futureSecurity = [...futuresExpiries.current, ...futuresExpiries.past].find((f) => getSecurityKey(f.security?.exchange ?? 0, f.security?.code ?? '') === securityKey)
            if(futureSecurity){
                newOrder = {
                    type: type,
                    quantity: quantity,
                    contractType: 'FUTURE',
                    uuid: uuid(),
                    expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                    data: {
                        security: futureSecurity.security,
                        key: securityKey,
                        numberOfDaysToExpiry: optionExpiries[expiryIndex].numberOfDaysToExpiry,
                        expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                        derived: futureSecurity.security?.derived
                    },
                    security: futureSecurity.security,
                    visibility: true
                } as OrdersDataType
            }
        }else if (expiryIndex >= 0) {
            if (optionExpiries[expiryIndex].trades.length <= 25) {
                const { optionsData } = await fetchOptionChainV2Initial({ exchange: exchangeFromJSON(underlyingToken.split(':')[0]), code: underlyingToken.split(':')[1] }, store.dispatch, true, [Number(optionExpiries[expiryIndex].expirySeconds)], optionExpiries)
                optionExpiries = optionsData
            }

            const data = (optionExpiries[expiryIndex]?.trades ?? optionExpiries[expiryIndex]?.trades).find((t) => t.strikeCol.strike === security?.strikePrice)
            if (data) {
                newOrder = {
                    type: type,
                    security: position.security,
                    callput: position.security?.code.includes('CE') ? 'call' : 'put',
                    quantity: quantity,
                    uuid: uuid(),
                    data,
                    expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                    strike: data.strikeCol.strike,
                    contractType: 'OPTION',
                    visibility: true
                } as OrdersDataType
            }
        }
        if(newOrder){
            newOrders.push(newOrder)
            basketOrderPrices.push({
                price: ((position.closePositionValue/position.closePosition)/(security?.priceMultiplier??100))?.toString(),
                topAsk: 0,
                topBid: 0,
                mid: 0,
                visibility: true
            })
            newParentSqOffOrder.push({
                ...newOrder,
                type: squarringOff ? (isBuy ? 'buy' : 'sell') : (isBuy ? 'sell' : 'buy'),
                orderDetails: {
                    price: position.closePositionValue/position.closePosition
                } as Order,
                uuid: uuid(),
                visibility: true
            })
        }
    }
    return { newOrder: newOrders, newParentSqOffOrder, basketOrderPrices }
}

export const getOrdersFromOpenOrders = async (orders: Order[], optionExpiries: optionsTradesDataType[], futuresExpiries:  StoreFutureExpiries, underlyingToken: string) => {
    const newOrders: OrdersDataType[] = []
    const newOrderPrices: OrderDataPrices[] = []
    for (let i = 0; i < orders.length; i++) {
        const position = orders[i]
        const secKey = getSecurityKey(position.security?.exchange??0, position.security?.code??'')
        const security = await getSecurityAsync(secKey)
        if(position.status !== OrderStatus.OPEN) continue
        const expiryIndex = optionExpiries.findIndex((e) => Number(e.expirySeconds) === security.expiry?.expiry)
        const isFuture = security.categoryCode === SecurityCategory_CategoryCode.FUTURES
        const securityKey = getSecurityKey(position.security?.exchange ?? 0, position.security?.code ?? '')
        const lotSize = security.lotSize ?? 50
        const type = position.transactionType === TransactionType.BUY ? 'buy' : 'sell'
        const quantity = position.quantity / lotSize
        let newOrder : OrdersDataType | undefined = undefined

        if(isFuture && futuresExpiries){
            const futureSecurity = [...futuresExpiries.current, ...futuresExpiries.past].find((f) => getSecurityKey(f.security?.exchange ?? 0, f.security?.code ?? '') === securityKey)
            if(futureSecurity){
                newOrder = {
                    type: type,
                    quantity: quantity,
                    contractType: 'FUTURE',
                    uuid: uuid(),
                    expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                    orderDetails: position,
                    data: {
                        security: futureSecurity.security,
                        key: securityKey,
                        numberOfDaysToExpiry: optionExpiries[expiryIndex].numberOfDaysToExpiry,
                        expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                        derived: futureSecurity.security?.derived
                    },
                    security: futureSecurity.security,
                    visibility: true
                } as OrdersDataType
            }
        }else if (expiryIndex >= 0) {
            if (optionExpiries[expiryIndex].trades.length <= 25) {
                const { optionsData } = await fetchOptionChainV2Initial({ exchange: exchangeFromJSON(underlyingToken.split(':')[0]), code: underlyingToken.split(':')[1] }, store.dispatch, true, [Number(optionExpiries[expiryIndex].expirySeconds)], optionExpiries)
                optionExpiries = optionsData
            }

            const data = (optionExpiries[expiryIndex]?.trades ?? optionExpiries[expiryIndex]?.trades).find((t) => t.strikeCol.strike === security?.strikePrice)
            if (data) {
                newOrder = {
                    type: type,
                    security: position.security,
                    callput: position.security?.code.includes('CE') ? 'call' : 'put',
                    quantity: quantity,
                    orderDetails: position,
                    uuid: uuid(),
                    data,
                    expiry: optionExpiries[expiryIndex].expiration.expiry?.expiry ?? 0,
                    strike: data.strikeCol.strike,
                    contractType: 'OPTION',
                    visibility: true
                } as OrdersDataType
            }
        }
        if(newOrder){
            newOrders.push(newOrder)
            newOrderPrices.push({
                price: ((newOrder.orderDetails?.price??0)/(security?.priceMultiplier??100))?.toString(),
                topAsk: 0,
                topBid: 0,
                mid: 0,
                visibility: true
            })
        }
    }
    return { newOrders, newOrderPrices }
}

export const getDisplayNameForPositionSecurity = (security: Security |undefined): string => {

    let result = security?.symbol ?? ''
    if (security?.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
        const expiry = new Date(security?.expiry?.expiry ?? 0)
        const day = expiry.getDate()
        const month = expiry.toLocaleString('en', { month: 'short' }).toUpperCase()
        const year = expiry.getFullYear()
        const currentYear = new Date().getFullYear()
        if (year === currentYear) {
            result = `${day} ${month}`
        } else {
            result = `${day} ${month} ${year.toString().slice(-2)}`
        }
        result += ` ${(security.strikePrice ?? 0) / (security.priceMultiplier ?? 100)} ${security.optionType === OptionType.CE ? 'CE' : 'PE'}`
    }
    return result 
}

/**
 * Drop basket child rows that only correspond to SL/TP/CE/CX orders (no main leg in history for that symbol).
 * Avoids showing CE/CX as fake “legs” under basket positions.
 */
export function filterBasketPositionChildrenHidingAuxiliaryOnlyLegs<T extends GroupedPosition>(
    children: T[] | undefined,
    basketGroupId: string,
    orderHistory: Order[] | undefined
): T[] {
    if (!children?.length) return [];
    return children.filter((ch) => {
        const key = getSecurityKey(ch.security.exchange, ch.security.code);
        const related = (orderHistory || []).filter(
            (o) =>
                o.basketId === basketGroupId &&
                getSecurityKey(o.security?.exchange ?? 0, o.security?.code ?? "") === key
        );
        if (related.length === 0) return true;
        return related.some((o) => !ORDER_TYPES_STRATEGY_AUXILIARY.includes(o.orderType));
    });
}

export const getGroupedPositions = (positions: Position[], groupBy: PositionsStateType['groupBy'] = "basket") => {
    const grouped: Record<string, GroupedPosition> = positions.reduce((acc, position) => {
        let groupId = position.basketId;

        if (groupBy === "symbol") {
            groupId = getSecurityKey(position.security.exchange, position.security.code);
        }

        const openQuantity = position.closePosition;

        const childPosition: GroupedPosition = {
            groupId,
            groupName: position.strategyGroup,
            security: position.security,
            quantity: position.closePosition,
            avgPrice: openQuantity !== 0 ? position.closePositionValue/openQuantity : 0,
            realizedProfit: position.realisedProfit,
            children: [],
            originalPosition: position
        }

        if (!acc[groupId]) {
            acc[groupId] = {
                groupId,
                groupName: position.strategyGroup,
                security: position.security,
                quantity: 0,
                avgPrice: 0,
                realizedProfit: 0,
                children: []
            }
        }

        // Combine multiple exchange/ledger rows for the same security within one basket
        // (basket strategies and iceberg `default_*` baskets that share the same symbol)
        if (groupBy === "basket" && position.basketId) {
            const securityKey = getSecurityKey(position.security.exchange, position.security.code);
            const existingChildIndex = acc[groupId].children.findIndex(child =>
                getSecurityKey(child.security.exchange, child.security.code) === securityKey
            );

            if (existingChildIndex >= 0) {
                // Combine with existing position for the same security
                const existingChild = acc[groupId].children[existingChildIndex];
                const combinedQuantity = existingChild.quantity + childPosition.quantity;
                const combinedValue = (existingChild.avgPrice * existingChild.quantity) + (childPosition.avgPrice * childPosition.quantity);

                acc[groupId].children[existingChildIndex] = {
                    ...existingChild,
                    quantity: combinedQuantity,
                    avgPrice: combinedQuantity !== 0 ? combinedValue / combinedQuantity : 0,
                    realizedProfit: existingChild.realizedProfit + childPosition.realizedProfit
                };
            } else {
                // Add as new child position
                acc[groupId].children.push(childPosition);
            }
        } else {
            // Regular grouping - add as new child
            acc[groupId].children.push(childPosition);
        }

        return acc;
    }, {} as Record<string, GroupedPosition>)

    Object.values(grouped).forEach(g => {
        grouped[g.groupId] = getAggregatedGroupedPosition(g)
    });
    return grouped
}

export const getAnalyzedGroupedPosition = (
    positions: Position[],
    basketId: string,
    placedOrders: Order[],
    tradeOrders: TradeOrder[]
): AnalyzeGroupedPosition | null => {
    const analyzedPositions = positions.filter((p) => p.basketId === basketId)

    if (analyzedPositions.length === 0) {
        return null
    }

    const orderIdToOrderMap: Record<string, Order> = placedOrders.reduce((map, order) => {
        map[order.orderId] = order;
        return map;
    }, {} as Record<string, Order>);

    const securityToOrderMap: Record<string, TradeOrder[]> = {};

    tradeOrders.forEach(order => {
        if (!securityToOrderMap[order.security.code]) {
            securityToOrderMap[order.security.code] = [];
        }
        if (!order.selected) return;

        const referencedOrder = order.referenceOrderId
            ? orderIdToOrderMap[order.referenceOrderId]
            : undefined;
        const shouldProject = !referencedOrder || referencedOrder.status !== OrderStatus.EXECUTED;

        // Unplaced builder rows legitimately have no referenceOrderId; only a reference that is set
        // yet unresolvable indicates a broken link that would project forever.
        if (order.referenceOrderId && !referencedOrder) {
            console.warn('[OrderSync][ProjectionReferenceUnresolvable]', {
                basketId,
                securityCode: order.security.code,
                tradeOrderClientOrderId: order.clientOrderId,
                referenceOrderId: order.referenceOrderId,
                placedOrderCount: placedOrders.length,
                tradeOrderQuantity: order.quantity,
            });
        }

        console.log('[OrderSync][ProjectionDecision]', {
            basketId,
            securityCode: order.security.code,
            tradeOrderClientOrderId: order.clientOrderId,
            unplacedBuilderRow: !order.referenceOrderId,
            referenceOrderId: order.referenceOrderId,
            referencedOrderStatus: referencedOrder?.status,
            referencedOrderQuantity: referencedOrder?.quantity,
            referencedOrderFilledQuantity: referencedOrder?.filledQuantity,
            tradeOrderQuantity: order.quantity,
            shouldProject,
        });

        if (shouldProject) {
            securityToOrderMap[order.security.code].push(order);
        }
    });

    const firstPosition = analyzedPositions[0];
    const groupedPosition: AnalyzeGroupedPosition = {
        groupId: basketId,
        groupName: firstPosition.strategyGroup,
        security: firstPosition.security,
        quantity: 0,
        avgPrice: 0,
        realizedProfit: 0,
        children: []
    }
    analyzedPositions.forEach((position) => {        
        const grouped: AnalyzeGroupedPosition = {
            groupId: basketId,
            groupName: position.strategyGroup,
            security: position.security,
            quantity: position.closePosition,
            avgPrice: position.closePosition !== 0 ? position.closePositionValue/position.closePosition : 0,
            realizedProfit: position.realisedProfit,
            children: [],
            originalPosition: position
        }

        groupedPosition.children.push(grouped);
    })

    const childPositions = [...(groupedPosition.children || [])];
    groupedPosition.children = [];

    // Track which securities have been processed
    const processedSecurities = new Set<string>();

    childPositions?.forEach(pos => {
        processedSecurities.add(pos.security.code);
        const orders = securityToOrderMap[pos.security.code] || [];
        let remainingQuantity = pos.quantity;
        let squaredOffQuantity = 0;
        let addedQuantity = 0;
        let avgPrice = pos.avgPrice;
        let realizedProfit = pos.realizedProfit;
        if (!orders.length || orders.length > 1) {
            groupedPosition.children.push({
                ...pos,
                squaredOffQuantity: 0,
                addedQuantity: 0,
                newQuantity: pos.quantity,
                children: []
            })
            return;
        }
        const order = orders[0];
        const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
        if (Math.sign(pos.quantity) !== Math.sign(direction)) {
            // Squaring off position
            const orderSqOffQuantity = Math.min(Math.abs(remainingQuantity), Math.abs(order.quantity));
            remainingQuantity = remainingQuantity + direction * order.quantity;
            squaredOffQuantity += orderSqOffQuantity;

            if ( orderSqOffQuantity < order.quantity ) {
                avgPrice = order.price;
            }
        } else {
            // Adding to position
            addedQuantity = order.quantity;
            let sum = Math.abs(remainingQuantity)*avgPrice + order.quantity*order.price;
            avgPrice = sum / (Math.abs(remainingQuantity) + Math.abs(order.quantity));
            remainingQuantity += order.quantity*direction;
            console.log('[OrderSync][PositionQuantityProjected]', {
                basketId,
                securityCode: pos.security.code,
                tradeOrderClientOrderId: order.clientOrderId,
                referenceOrderId: order.referenceOrderId,
                referencedOrderStatus: order.referenceOrderId
                    ? orderIdToOrderMap[order.referenceOrderId]?.status
                    : undefined,
                positionQuantity: pos.quantity,
                projectedOrderQuantity: order.quantity * direction,
                displayedNewQuantity: remainingQuantity,
            });
        }
        if (remainingQuantity === 0) {
            groupedPosition.children.push({
                ...pos,
                squareOffOrderId: order.clientOrderId,
                squaredOffQuantity,
                addedQuantity: 0,
                newQuantity: 0,
                avgPrice: pos.avgPrice, // Preserve original avgPrice for strategy-level calculations
                realizedProfit,
                children: []
            })
        } else if (Math.sign(remainingQuantity) === Math.sign(pos.quantity)) {
            // Same direction - either adding or partially squaring off
            groupedPosition.children.push({
                ...pos,
                squareOffOrderId: squaredOffQuantity > 0 ? order.clientOrderId : undefined,
                addOrderId: addedQuantity > 0 ? order.clientOrderId : undefined,
                squaredOffQuantity,
                addedQuantity,
                newQuantity: remainingQuantity,
                avgPrice,
                realizedProfit,
                children: []
            })
        } else {
            // completely squared off and reversed
            groupedPosition.children.push({
                ...pos,
                squareOffOrderId: order.clientOrderId,
                squaredOffQuantity: Math.abs(pos.quantity),
                addedQuantity: 0,
                newQuantity: remainingQuantity,
                avgPrice,
                realizedProfit,
                children: []
            })
        }
    })

    const newOrders: TradeOrder[] = [];
    Object.keys(securityToOrderMap).forEach(securityCode => {
        if (!processedSecurities.has(securityCode)) {
            const orders = securityToOrderMap[securityCode];
            newOrders.push(...orders);
        }
    })

    newOrders.forEach(order => {
        const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
        const orderQuantity = direction * order.quantity;

        groupedPosition.children.push({
            groupId: basketId,
            groupName: groupedPosition.groupName,
            security: order.security,
            quantity: 0,
            avgPrice: order.price,
            realizedProfit: 0,
            squaredOffQuantity: 0,
            addedQuantity: order.quantity,
            newQuantity: orderQuantity,
            addOrderId: order.clientOrderId,
            children: [],
            new: true
        });
    });

    return getAggregatedAnalyzedGroupedPosition(groupedPosition, newOrders)
}

export const getAggregatedGroupedPosition = (groupedPosition: GroupedPosition): GroupedPosition => {
    const quantity = groupedPosition.children?.length > 1 ? calculateGcd(groupedPosition.children?.map(p => p.quantity) || []) : groupedPosition.children[0]?.quantity
    const premium = groupedPosition.children?.reduce((sum, p) => sum + p.avgPrice * p.quantity, 0) || 0

    // Calculate avgPrice with protection against division by zero
    let avgPrice = 0;
    if (groupedPosition.children?.length > 1) {
        avgPrice = quantity !== 0 ? Math.abs(premium / quantity) : 0;
    } else {
        avgPrice = groupedPosition.children[0]?.avgPrice || 0;
    }

    return {
        ...groupedPosition,
        quantity,
        realizedProfit: groupedPosition.children?.reduce((sum, p) => sum + p.realizedProfit, 0) || 0,
        avgPrice
    };
}

export const getAggregatedAnalyzedGroupedPosition = (groupedPosition: AnalyzeGroupedPosition, newOrders: TradeOrder[] = []): AnalyzeGroupedPosition => {
    const openPositions = groupedPosition.children?.filter(p => p.newQuantity && p.newQuantity !== 0) || [];
    const quantities = (openPositions?.map(p => p.newQuantity) || []).sort();
    let quantity = Math.abs(openPositions?.length > 1 ? calculateGcd(quantities) : openPositions[0]?.newQuantity || 0)
    let premium = openPositions?.reduce((sum, p) => sum + p.avgPrice * p.newQuantity, 0) || 0

    if (newOrders.length > 0 && openPositions.length === 0) {
        newOrders.forEach(order => {
            const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
            const orderQuantity = direction * order.quantity;
            const orderPremium = order.price * orderQuantity;

            premium += orderPremium;
            quantities.push(orderQuantity);
        })

        quantity = Math.abs(quantities.length > 1 ? calculateGcd(quantities.map(q => Math.abs(q)).sort()) : quantities[0] || 0)
    }

    let avgPrice = 0
    if (quantity === 0 || (!openPositions.length && newOrders.length === 0)) {
        const allPositions = groupedPosition.children || []
        const originalQuantities = allPositions.map(p => p.quantity).sort()
        const originalQuantity = Math.abs(allPositions.length > 1 ? calculateGcd(originalQuantities) : allPositions[0]?.quantity || 0)
        const originalPremium = allPositions.reduce((sum, p) => sum + p.avgPrice * p.quantity, 0)

        // Protect against division by zero
        if (allPositions.length > 1) {
            avgPrice = originalQuantity !== 0 ? Math.abs(originalPremium / originalQuantity) : 0;
        } else {
            avgPrice = allPositions[0]?.avgPrice || 0;
        }
    } else {
        // Protect against division by zero
        avgPrice = quantity !== 0 ? Math.abs(premium / quantity) : 0;
    }

    const next: AnalyzeGroupedPosition = {
        ...groupedPosition,
        children: groupedPosition.children ?? [],
        quantity: premium < 0 ? -quantity : quantity,
        newQuantity: premium < 0 ? -quantity : quantity,
        realizedProfit: groupedPosition.children?.reduce((sum, p) => sum + p.realizedProfit, 0) || 0,
        avgPrice,
    }
    return next
}

export const getSquareOffOrder = (position: GroupedPosition, price?: number): TradeOrder => {
    return {
        transactionType: position.quantity > 0 ? TransactionType.SELL : TransactionType.BUY,
        quantity: Math.abs(position.quantity),
        security: position.security,
        price: price || 0,
        clientOrderId: uuid(),
        selected: true
    }
}
export const getPositionProduct = (position: GroupedPosition) => {
    if (position.originalPosition?.product) {
        return position.originalPosition.product;
    }
    if (position.children?.length > 0) {
        const activeChild = position.children.find(child => child.quantity !== 0 && child.originalPosition?.product);
        if (activeChild?.originalPosition?.product) {
            return activeChild.originalPosition.product;
        }
        
        const firstChildWithProduct = position.children.find(child => child.originalPosition?.product);
        if (firstChildWithProduct?.originalPosition?.product) {
            return firstChildWithProduct.originalPosition.product;
        }
    }
    return 0;
}